import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ZonaCargando from "../alertas/zonaCargando";
import { formatearMoneda } from "../../funcionesDatos/Moneda";

function fmtM(n) { return formatearMoneda(Number(n || 0), "COP"); }
function fmtF(f) {
    if (!f) return "—";
    return new Date(f).toLocaleString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function val(v) { return (!v || v === "NA" || v === "0" || v === 0) ? null : v; }

const ESTADO_DESEMBOLSO = {
    pendiente:  { label: "Pendiente",  cls: "bg-warning-subtle text-warning-emphasis border border-warning-subtle" },
    programado: { label: "Programado", cls: "bg-primary-subtle text-primary-emphasis border border-primary-subtle" },
    pagado:     { label: "Pagado",     cls: "bg-success-subtle text-success-emphasis border border-success-subtle" },
    retenido:   { label: "Retenido",   cls: "bg-danger-subtle text-danger-emphasis border border-danger-subtle" },
    fallido:    { label: "Fallido",    cls: "bg-danger-subtle text-danger-emphasis border border-danger-subtle" },
    cancelado:  { label: "Cancelado",  cls: "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle" },
};

function Fila({ label, children, value }) {
    const contenido = children || (value ? <span className="small fw-semibold text-dark">{value}</span> : null);
    if (!contenido) return null;
    return (
        <div className="d-flex justify-content-between align-items-start py-2 border-bottom" style={{ borderColor: "rgba(var(--negro-rgb), 0.06)" }}>
            <span className="text-muted small">{label}</span>
            {contenido}
        </div>
    );
}

function TarjetaUsuario({ u, rol }) {
    if (!u) return null;
    const nombre = [u.nombre, u.apellido].filter(Boolean).join(" ") || u.username || "—";
    return (
        <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
                <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>{rol}</p>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <img src={u.avatar || "/imagenes/marca/avatar.png"} alt={nombre} className="rounded-circle flex-shrink-0" style={{ width: 44, height: 44, objectFit: "cover" }} />
                    <div>
                        <div className="fw-semibold text-dark">{nombre}</div>
                        <div className="text-muted small">@{u.username || "—"} · {u.email || "—"}</div>
                    </div>
                </div>
                {u.username && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill px-3 w-100"
                        onClick={() => {
                            localStorage.setItem("admin_nav_usuario", JSON.stringify({ username: u.username }));
                            window.open("/admin/usuario", "_blank");
                        }}
                    >
                        <i className="bi bi-person-badge me-1"></i>Ver perfil
                    </button>
                )}
            </div>
        </div>
    );
}

function PagoAdminIndividual() {
    const location = useLocation();
    const navigate = useNavigate();
    const { idReserva } = location.state?.params || {};

    const [reserva, setReserva]     = useState(null);
    const [comprador, setComprador] = useState(null);
    const [proveedor, setProveedor] = useState(null);
    const [servicio, setServicio]   = useState(null);
    const [cargando, setCargando]   = useState(true);

    useEffect(() => {
        if (!idReserva) {
            Swal.fire({ icon: "error", title: "Sin datos", text: "No se especificó la transacción.", confirmButtonColor: "var(--azul-logo-fondo)" });
            navigate(-1);
            return;
        }
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/obtener-reserva-admin-completa", { params: { idReserva } });
                if (!res.data.exito) {
                    Swal.fire({ icon: "error", title: "Error", text: res.data.mensaje || "No se pudo cargar.", confirmButtonColor: "var(--azul-logo-fondo)" });
                    return navigate(-1);
                }
                setReserva(res.data.reservaActual);
                setComprador(res.data.comprador);
                setProveedor(res.data.proveedor);
                setServicio(res.data.servicioActual);
            } catch {
                Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar la transacción.", confirmButtonColor: "var(--azul-logo-fondo)" });
                navigate(-1);
            } finally {
                setCargando(false);
            }
        })();
    }, [idReserva, navigate]);

    if (cargando) return <ZonaCargando />;
    if (!reserva) return null;

    const cobro      = reserva.cobro      || {};
    const desembolso = reserva.desembolso || {};
    const comision   = reserva.comision   || {};
    const pasarela   = reserva.pasarela   || {};
    const esUrgente  = cobro.esUrgente && Number(cobro.montoUrgencia || 0) > 0;
    const montoUrg   = Number(cobro.montoUrgencia || 0);
    const totalVend  = Number(desembolso.montoProveedor || 0) + (esUrgente ? montoUrg : 0);
    const estD       = ESTADO_DESEMBOLSO[desembolso.estado] || ESTADO_DESEMBOLSO.pendiente;

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-start pt-3 pb-2 mb-4 border-bottom flex-wrap gap-2">
                <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <h1 className="h2 text-dark mb-0">Transacción</h1>
                        <span className="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle px-3">
                            <i className="bi bi-check-circle-fill me-1"></i>Pago aprobado
                        </span>
                        {esUrgente && (
                            <span className="badge rounded-pill bg-danger-subtle text-danger border border-danger-subtle px-3" style={{ fontSize: "0.72rem" }}>
                                <i className="bi bi-lightning-fill me-1"></i>Urgente
                            </span>
                        )}
                    </div>
                    <p className="text-muted small mb-0" style={{ fontFamily: "monospace", fontSize: "0.72rem" }}>ID: {idReserva}</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-outline-secondary btn-sm rounded-3" onClick={() => navigate(-1)}>
                        <i className="bi bi-arrow-left me-1"></i>Volver
                    </button>
                    <button className="btn btn-outline-primary btn-sm rounded-3" onClick={() => navigate("/admin/reserva-admin", { state: { params: { idReserva } } })}>
                        <i className="bi bi-calendar-check me-1"></i>Ver reserva completa
                    </button>
                </div>
            </div>

            <div className="row g-4">
                {/* Izquierda: desglose financiero */}
                <div className="col-12 col-lg-7 d-flex flex-column gap-4">

                    {/* Servicio */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-semibold text-uppercase mb-2" style={{ letterSpacing: "0.05em" }}>
                                <i className="bi bi-briefcase me-1"></i>Servicio
                            </p>
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                    <div className="fw-bold text-dark" style={{ fontSize: "1rem" }}>
                                        {reserva.nombreServicio || servicio?.nombre || "—"}
                                    </div>
                                    <div className="text-muted small mt-1 d-flex gap-3 flex-wrap">
                                        {val(reserva.tipoPaquete) && <span><i className="bi bi-box me-1"></i>{reserva.tipoPaquete}</span>}
                                        {val(servicio?.categoria) && <span><i className="bi bi-tag me-1"></i>{servicio.categoria}</span>}
                                    </div>
                                </div>
                                {val(reserva.idServicio) && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary rounded-pill px-3 flex-shrink-0"
                                        onClick={() => {
                                            localStorage.setItem("admin_nav_servicio", JSON.stringify({ idServicio: reserva.idServicio }));
                                            window.open("/admin/servicio", "_blank");
                                        }}
                                    >
                                        <i className="bi bi-box-arrow-up-right me-1"></i>Ver servicio
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Desglose cobro */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>
                                <i className="bi bi-receipt me-1"></i>Desglose del cobro
                            </p>

                            <Fila label="Precio base (proveedor)" value={fmtM(cobro.precioBaseProveedor)} />
                            {esUrgente && (
                                <Fila label="Recargo urgencia">
                                    <span className="small fw-semibold text-danger">+{fmtM(montoUrg)}</span>
                                </Fila>
                            )}
                            <Fila label="Costo pasarela"                                                    value={fmtM(pasarela.monto)} />
                            <Fila label={`Comisión Zervicy (${((comision.porcentaje || 0) * 100).toFixed(0)}%)`} value={fmtM(comision.monto)} />
                            <Fila label="Total costo servicio"                                              value={fmtM(cobro.costoServicio)} />

                            <div className="d-flex justify-content-between align-items-center p-3 mt-2 rounded-3" style={{ background: "rgba(var(--azul-bootstrap-cta-rgb), 0.05)" }}>
                                <span className="fw-bold text-dark">Total pagado por comprador</span>
                                <span className="fw-bold text-dark" style={{ fontSize: "1.15rem" }}>{fmtM(cobro.montoTotal)}</span>
                            </div>

                            <hr className="my-3" />

                            <Fila label={`Comisión Zervicy (${((comision.porcentaje || 0) * 100).toFixed(0)}%)`}>
                                <span className="small fw-semibold text-dark">{fmtM(comision.monto)}</span>
                            </Fila>

                            <Fila label="Neto al vendedor">
                                <div className="text-end">
                                    <span className="small fw-bold text-success">{fmtM(totalVend)}</span>
                                    {esUrgente && (
                                        <div className="text-muted" style={{ fontSize: "0.68rem" }}>
                                            base {fmtM(desembolso.montoProveedor)} + urgencia {fmtM(montoUrg)}
                                        </div>
                                    )}
                                </div>
                            </Fila>
                        </div>
                    </div>

                    {/* Método de pago */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>
                                <i className="bi bi-credit-card me-1"></i>Método de pago
                            </p>
                            <Fila label="Pasarela"       value={val(cobro.gateway) || "Wompi"} />
                            <Fila label="Método"         value={val(cobro.metodoPagoTipo)} />
                            <Fila label="Detalle"        value={val(cobro.metodoPagoExtra1)} />
                            <Fila label="Banco / Extra"  value={val(cobro.metodoPagoExtra2)} />
                            <Fila label="ID transacción" value={val(cobro.idPago)} />
                            <Fila label="Referencia"     value={val(cobro.referencia)} />
                            <Fila label="Fecha de pago"  value={fmtF(cobro.fechaPago)} />
                            <Fila label="Moneda"         value={cobro.moneda || "COP"} />
                        </div>
                    </div>
                </div>

                {/* Derecha: desembolso + partes */}
                <div className="col-12 col-lg-5 d-flex flex-column gap-4">

                    {/* Desembolso */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="text-muted small fw-semibold text-uppercase mb-0" style={{ letterSpacing: "0.05em" }}>
                                    <i className="bi bi-bank2 me-1"></i>Desembolso al vendedor
                                </p>
                                <span className={`badge rounded-pill px-3 py-2 ${estD.cls}`} style={{ fontSize: "0.75rem" }}>{estD.label}</span>
                            </div>

                            <div className="text-center py-3 mb-3 rounded-3" style={{ background: "rgba(var(--verde-bootstrap-rgb), 0.06)" }}>
                                <div className="text-muted small mb-1">Monto a desembolsar</div>
                                <div className="fw-bold text-success" style={{ fontSize: "1.6rem" }}>{fmtM(totalVend)}</div>
                                {esUrgente && (
                                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                        base {fmtM(desembolso.montoProveedor)} + urgencia {fmtM(montoUrg)}
                                    </div>
                                )}
                            </div>

                            <Fila label="Disponible desde"   value={fmtF(desembolso.disponibleDesde)} />
                            <Fila label="Fecha programada"   value={fmtF(desembolso.fechaProgramada)} />
                            <Fila label="Fecha pagado"       value={fmtF(desembolso.fechaPagado)} />
                            <Fila label="Referencia pago"    value={val(desembolso.referenciaPago)} />
                            <Fila label="Wompi Payout ID"    value={val(desembolso.wompiPayoutId)} />
                            <Fila label="Notas"              value={val(desembolso.notas)} />

                            {reserva.reclamo?.abierto && (
                                <div className="alert alert-danger rounded-3 mt-3 mb-0 py-2 px-3 small">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    Reclamo abierto — desembolso bloqueado.
                                </div>
                            )}
                        </div>
                    </div>

                    <TarjetaUsuario u={comprador} rol="Comprador" />
                    <TarjetaUsuario u={proveedor}  rol="Vendedor" />
                </div>
            </div>
        </div>
    );
}

export default PagoAdminIndividual;
